import { v4 as uuidv4 } from 'uuid';
import { getAuthorityTypeForField } from '../../../constants';

// Default authority source - temporary fixed value
const DEFAULT_AUTHORITY_SOURCE = 'Imamu1';

/**
 * Get default content for 008 field (fixed-length data elements)
 */
const getDefault008Content = () => ({
  'Undef_18': '\\\\\\\\\\\\\\\\\\\\',
  'Undef_30': '\\',
  'Undef_34': '\\\\\\\\',
  'Geo Subd': '\\',
  'Roman': '\\',
  'Lang': '\\',
  'Kind rec': '\\',
  'Cat Rules': '\\',
  'SH Sys': '\\',
  'Series': '\\',
  'Numb Series': '\\',
  'Main use': '\\',
  'Subj use': '\\',
  'Series use': '\\',
  'Type Subd': '\\',
  'Govt Ag': '\\',
  'RefEval': '\\',
  'RecUpd': '\\',
  'Pers Name': '\\',
  'Level Est': '\\',
  'Mod Rec Est': '\\',
  'Source': '\\',
});

/**
 * Get MARC tag for authority heading based on authority type
 */
const getHeadingTagFromAuthorityType = (authorityType) => {
  const mapping = {
    'personalName': '100',
    'corporateName': '110',
    'meetingName': '111',
    'uniformTitle': '130',
    'subject': '150',
    'geographicName': '151'
  };
  return mapping[authorityType] || '100';
};

/**
 * Determine authority type from field path and form context
 */
const determineAuthorityType = (fieldPath, formValues, arrayIndex, controlledVocab) => {
  const authorityConfig = getAuthorityTypeForField(fieldPath, formValues, arrayIndex, controlledVocab);
  return authorityConfig?.searchType || 'personalName';
};

/**
 * Build authority record payload for API
 */
export const buildAuthorityRecord = (textValue, fieldPath, formValues = {}, arrayIndex = 0, controlledVocab = {}, authoritySource = null) => {
  if (!textValue || textValue.trim().length < 3) {
    throw new Error('Text value must be at least 3 characters');
  }

  const authorityType = determineAuthorityType(fieldPath, formValues, arrayIndex, controlledVocab);
  const headingTag = getHeadingTagFromAuthorityType(authorityType);
  const sourceToUse = authoritySource || DEFAULT_AUTHORITY_SOURCE;

  const recordId = uuidv4();
  const parsedRecordId = uuidv4();

  const recordData = {
    externalId: recordId,
    leader: '00000nz\\\\a2200000o\\\\4500',
    fields: [
      {
        tag: '001',
        content: sourceToUse || DEFAULT_AUTHORITY_SOURCE
      },
      {
        tag: '008',
        content: getDefault008Content()
      },
      {
        tag: headingTag,
        content: `$a ${textValue.trim()}`,
        indicators: ['\\', '\\']
      }
    ],
    parsedRecordDtoId: recordId,
    parsedRecordId,
    relatedRecordVersion: 1,
    marcFormat: 'AUTHORITY',
    suppressDiscovery: false,
    updateInfo: { recordState: 'NEW' },
    _actionType: 'create'
  };



  return recordData;
};

/**
 * Get current authority source (for future settings integration)
 */
export const getAuthoritySource = () => {
  // TODO: Get from settings in the future
  return DEFAULT_AUTHORITY_SOURCE;
};

/**
 * Format authority record response for linking
 */
export const formatAuthorityForLinking = (response, textValue, fieldPath, formValues, arrayIndex, controlledVocab) => {


  // Extract the ID from various possible response formats
  const authorityId = response.qmRecordId || response.externalId || response.id || response.parsedRecordId;

  // Determine the correct authority type for this field
  const authorityType = determineAuthorityType(fieldPath, formValues, arrayIndex, controlledVocab);

  if (!authorityId || !textValue) {
    console.error('Missing required fields for authority linking:', {
      authorityId,
      textValue,
      responseKeys: Object.keys(response),
      fullResponse: response
    });
    throw new Error('Invalid authority record: missing required fields');
  }

  return {
    // Primary fields for linking (used by handleLinkAuthority)
    id: authorityId,
    headingRef: textValue, // Required field
    // Additional fields for logging and compatibility
    authorityId: authorityId,
    heading: textValue,
    text: textValue,
    authRefType: 'Authorized',
    naturalId: response.naturalId || authorityId,
    displayText: textValue,
    sourceFileId: response.sourceFileId || DEFAULT_AUTHORITY_SOURCE,
    headingType: authorityType
  };
};
